import { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, CircularProgress, Grid, Paper, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import InventoryIcon from "@mui/icons-material/Inventory";
import SearchIcon from "@mui/icons-material/Search";
import DataGridViewport from "../components/DataGridViewport";
import { useUserContext } from "../hooks/UserContext";
import LocalSelector from "./components/LocalSelector";
import { fetchGetAuditoriaStock, AuditoriaStockItem } from "../services/reporteService";
import { isAbortError } from "../services/fetchService";
import { fetchGetProductosParaVenta } from "../services/productoService";
import { Producto } from "../models/ProductoModel";

const today = new Date();
const formatInputDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

const columns: GridColDef[] = [
  { field: "fecha", headerName: "Fecha", width: 110, valueFormatter: (value) => new Date(value).toLocaleDateString("es-AR") },
  { field: "hora", headerName: "Hora", width: 90 },
  { field: "operacion", headerName: "Operación", width: 170 },
  { field: "producto", headerName: "Producto", minWidth: 180, flex: 1 },
  { field: "descripcion", headerName: "Descripción", minWidth: 200, flex: 1 },
  { field: "forma", headerName: "Forma", width: 150 },
  { field: "cantidadForma", headerName: "Cant. forma", width: 120 },
  { field: "unidadesPorForma", headerName: "Unidades/forma", width: 130 },
  { field: "cantidad", headerName: "Movimiento (base)", width: 150 },
  { field: "stockAntes", headerName: "Stock antes", width: 120 },
  { field: "stockDespues", headerName: "Stock después", width: 130 },
  { field: "origen", headerName: "Origen", width: 140 },
  { field: "idOrigen", headerName: "Id origen", width: 100 },
  { field: "usuario", headerName: "Usuario", width: 130 },
];

const AuditoriaStock = () => {
  const { user } = useUserContext();
  const [idLocal, setIdLocal] = useState<string>();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [producto, setProducto] = useState<Producto | null>(null);
  const [desde, setDesde] = useState(formatInputDate(firstDay));
  const [hasta, setHasta] = useState(formatInputDate(today));
  const [items, setItems] = useState<AuditoriaStockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consulta, setConsulta] = useState(0);

  useEffect(() => {
    if (!user || !idLocal) {
      setProductos([]);
      setProducto(null);
      return;
    }
    const controller = new AbortController();
    fetchGetProductosParaVenta(idLocal, user, controller.signal)
      .then((resultado: Producto[] | string) => setProductos(typeof resultado === "string" ? JSON.parse(resultado) : resultado))
      .catch((error) => { if (!isAbortError(error)) setProductos([]); });
    return () => controller.abort();
  }, [idLocal, user]);

  useEffect(() => {
    if (!user || !idLocal || !consulta) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const idProducto = producto?.idProducto ?? producto?.IDPRODUCTO;
    fetchGetAuditoriaStock(user, Number(idLocal), desde, hasta, idProducto, controller.signal)
      .then(setItems)
      .catch((err) => {
        if (isAbortError(err)) return;
        setItems([]);
        setError("No se pudo cargar la auditoría de stock.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [consulta, desde, hasta, idLocal, producto?.idProducto, user]);

  const buscar = () => setConsulta((current) => current + 1);

  return (
    <Box sx={{ py: 2 }}>
      <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Grid item><InventoryIcon color="primary" /></Grid>
        <Grid item>
          <Typography variant="h4">Auditoría de stock</Typography>
          <Typography variant="body2" color="text.secondary">
            Los movimientos y saldos se expresan en unidades base. La forma aparece solo cuando quedó registrada en la operación.
          </Typography>
        </Grid>
      </Grid>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}><LocalSelector onLocalChange={setIdLocal} /></Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete options={productos} value={producto} getOptionLabel={(option) => `${option.CODIGO ?? option.codigo ?? ""} ${option.PRODUCTO ?? option.producto ?? ""}`}
              onChange={(_, value) => setProducto(value)} renderInput={(params) => <TextField {...params} label="Producto (opcional)" />} />
          </Grid>
          <Grid item xs={6} md={2}><TextField type="date" label="Desde" value={desde} onChange={(event) => setDesde(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></Grid>
          <Grid item xs={6} md={2}><TextField type="date" label="Hasta" value={hasta} onChange={(event) => setHasta(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></Grid>
          <Grid item xs={12} md={2}><Button variant="contained" onClick={buscar} startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <SearchIcon />} disabled={loading || !idLocal} fullWidth>Consultar</Button></Grid>
        </Grid>
      </Paper>
      {error ? <Alert severity="error">{error}</Alert> : (
        <DataGridViewport height={650} minWidth={1800}>
          <DataGrid rows={items.map((item, index) => ({ ...item, id: index }))} columns={columns} loading={loading} pageSizeOptions={[10, 20, 50]} initialState={{ pagination: { paginationModel: { page: 0, pageSize: 20 } } }} />
        </DataGridViewport>
      )}
    </Box>
  );
};

export default AuditoriaStock;
